import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type { Player } from '../../../types/backend';
import type {
  FortressStatus,
  Quest,
  GameTask,
  PlayerProfile,
  MorningBriefing,
  LeaderboardEntry,
} from '../types';

/** Map the backend Player aggregate to the frontend's flat PlayerProfile view-model. */
export function mapPlayerToProfile(player: Player): PlayerProfile {
  const stats = player.stats;
  return {
    user_id: player.id,
    display_name: player.display_name,
    xp: stats?.total_xp ?? 0,
    level: stats?.level ?? 1,
    active_streak_days: stats?.daily_streak ?? 0,
    mastery_tracks: Object.fromEntries(player.mastery.map((m) => [m.track, m.xp])),
  };
}

export const createGamificationService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getStatus: async (): Promise<FortressStatus> => {
      return rpcCall<FortressStatus>(client, 'game.GetStatus', {});
    },

    getCustomerHealth: async (customerId: string): Promise<{ health_score: number; active_quest_count: number }> => {
      // game.GetCustomerHealth has no response_model, so its RpcMethods result is
      // `unknown`; assert the ad-hoc result shape here (escape hatch).
      return rpcCall<{ health_score: number; active_quest_count: number }>(client, 'game.GetCustomerHealth', { customer_id: customerId });
    },

    listQuests: async (customerId?: string): Promise<Quest[]> => {
      return rpcCall(client, 'game.ListQuests', customerId ? { customer_id: customerId } : {});
    },

    getQuest: async (questId: string): Promise<Quest> => {
      return rpcCall(client, 'game.GetQuest', { quest_id: questId });
    },

    claimTask: async (questId: string, taskId: string): Promise<GameTask> => {
      return rpcCall(client, 'game.ClaimTask', { quest_id: questId, task_id: taskId });
    },

    completeTask: async (questId: string, taskId: string): Promise<GameTask> => {
      return rpcCall(client, 'game.CompleteTask', { quest_id: questId, task_id: taskId });
    },

    getPlayerProfile: async (): Promise<PlayerProfile> => {
      const player = await rpcCall(client, 'game.GetPlayerProfile', {});
      return mapPlayerToProfile(player);
    },

    getLeaderboard: async (): Promise<LeaderboardEntry[]> => {
      return rpcCall(client, 'game.GetLeaderboard', {});
    },

    // Backend types critical_tasks/quick_wins as loosely-typed dicts; MorningBriefing
    // here describes their actual runtime shape (see MorningBriefingTaskRef in ../types).
    getMorningBriefing: async (): Promise<MorningBriefing> => {
      return rpcCall<MorningBriefing>(client, 'game.GetMorningBriefing', {});
    },

    configureQuest: async (params: {
      title: string;
      type: string;
      customer_id?: string;
      priority?: string;
      deadline?: string;
      description?: string;
      [key: string]: unknown;
    }): Promise<Quest> => {
      return rpcCall(client, 'game.ConfigureQuest', params);
    },
  };
};

export const useGamificationService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return createGamificationService(idToken);
  }, [idToken]);
};
